import { api } from './client';

/**
 * Homingo membership plans.
 *
 * ---------------------------------------------------------------------------
 * BUYING IS TWO STEPS, AND THE FIRST ONE GRANTS NOTHING
 * ---------------------------------------------------------------------------
 * `purchase()` creates a subscription in `pending_payment`. It confers no
 * discount, no coin multiplier, nothing - until the payment confirmation
 * activates it. `checkout()` then opens Razorpay against that row.
 *
 * So a screen must never show "you're a member" off the back of `purchase()`
 * resolving. The only proof of membership is `active()` returning a
 * subscription, and that is what the UI should re-read after payment.
 *
 * ---------------------------------------------------------------------------
 * MONEY IS STRINGS
 * ---------------------------------------------------------------------------
 * `priceAmount`, `maxDiscountAmount`, `discountPercent` and `coinEarnMultiplier`
 * are Prisma `Decimal` columns and arrive as decimal strings. `perksFor` is the
 * exception: it pre-converts `discountPercent` and `coinEarnMultiplier` to
 * numbers because the booking screens do arithmetic with them.
 */

export type SubscriptionTier = string;

/** Set by the customer at purchase. `complimentary` is ops-granted only. */
export type SubscriptionPaymentMode = 'online' | 'cash' | 'complimentary';

/** A plan on the shelf - `GET /customers/me/subscriptions/plans`. */
export type SubscriptionPlan = {
  id: string;
  /** Stable machine code, e.g. `gold_monthly`. Not for display. */
  code: string;
  name: string;
  description: string | null;
  tier: SubscriptionTier;
  sortOrder: number;
  /** Decimal string. */
  priceAmount: string;
  durationDays: number;
  /** Decimal string, e.g. "10.00" for 10% off each booking. */
  discountPercent: string;
  /** Decimal string, or null for an uncapped discount. */
  maxDiscountAmount: string | null;
  /** Decimal string, e.g. "2.00" to earn coins at double rate. */
  coinEarnMultiplier: string;
  /** Credited once on activation. */
  bonusCoins: number;
  waivesCancellationFee: boolean;
  extraReschedules: number;
  priorityDispatch: boolean;
  /** Null means unlimited bookings at the plan discount. */
  includedBookings: number | null;
  cityId: string | null;
  isActive: boolean;
};

/** One subscription the customer holds or held. */
export type CustomerSubscription = {
  id: string;
  planId: string;
  /** `pending_payment` | `active` | `expired` | `cancelled`. */
  status: string;
  activatedAt: string | null;
  expiresAt: string | null;
  bookingsUsed: number;
  bookingsRemaining: number | null;
};

/**
 * The live plan flattened into what the booking screens need -
 * `GET /customers/me/subscriptions/active`.
 *
 * `subscriptionId: null` is the honest "no membership" answer and is what a
 * lapsed plan returns too, whatever its stored status says.
 */
export type ActiveSubscriptionPerks = {
  subscriptionId: string | null;
  planCode: string | null;
  planName: string | null;
  /** A NUMBER here, unlike on the plan. Zero once the allowance is spent. */
  discountPercent: number;
  /** Decimal string, or null when uncapped. */
  maxDiscountAmount: string | null;
  /** A NUMBER here too. Still applies after the allowance runs out. */
  coinEarnMultiplier: number;
  waivesCancellationFee: boolean;
  extraReschedules: number;
  /** Null means unlimited. Zero means the per-booking discount has stopped. */
  bookingsRemaining: number | null;
  expiresAt: string | null;
};

/** What Razorpay checkout needs, minted by the backend. */
export type SubscriptionCheckout = {
  orderId: string;
  razorpayOrderId: string;
  /** The publishable key. Never the secret - that stays server-side. */
  keyId: string;
  /** Decimal string. */
  amount: string;
  currency: string;
  purpose: string;
};

/**
 * Plans this customer can buy.
 *
 * Pass `cityId` from the address they book against most: city-scoped plans are
 * only listed for their own city, and omitting it hides them.
 */
export const plans = (
  cityId?: string,
  signal?: AbortSignal,
): Promise<SubscriptionPlan[]> =>
  api.get<SubscriptionPlan[]>(
    `/customers/me/subscriptions/plans${cityId ? `?cityId=${encodeURIComponent(cityId)}` : ''}`,
    { signal },
  );

/** The live plan and its perks. Check `subscriptionId` for membership. */
export const active = (
  signal?: AbortSignal,
): Promise<ActiveSubscriptionPerks> =>
  api.get<ActiveSubscriptionPerks>('/customers/me/subscriptions/active', {
    signal,
  });

/** Every subscription this customer has held, newest first. */
export const history = (
  signal?: AbortSignal,
): Promise<CustomerSubscription[]> =>
  api.get<CustomerSubscription[]>('/customers/me/subscriptions', { signal });

/**
 * Start buying a plan - step one of two.
 *
 * Creates a `pending_payment` row that grants nothing yet. Throws 409 if a
 * live plan already exists, which is the case worth catching by hand: the
 * customer should be told they are already a member, not shown a raw error.
 */
export const purchase = (
  planId: string,
  paymentMode: SubscriptionPaymentMode = 'online',
): Promise<CustomerSubscription> =>
  api.post<CustomerSubscription>('/customers/me/subscriptions', {
    planId,
    paymentMode,
  });

/** Open Razorpay against a pending subscription - step two. */
export const checkout = (
  subscriptionId: string,
): Promise<SubscriptionCheckout> =>
  api.post<SubscriptionCheckout>(
    `/customers/me/subscriptions/${subscriptionId}/checkout`,
  );

/** Stop a plan. The reason is required by the backend. */
export const cancel = (
  subscriptionId: string,
  reason: string,
): Promise<CustomerSubscription> =>
  api.post<CustomerSubscription>(
    `/customers/me/subscriptions/${subscriptionId}/cancel`,
    { reason },
  );
